import { useEffect, useRef } from 'react';
import { createPortal }     from 'react-dom';

interface ModalProps {
  open:       boolean;
  onClose:    () => void;
  title?:     string;
  width?:     'sm' | 'md' | 'lg';
  children:   React.ReactNode;
}

const WIDTH_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

export default function Modal({
  open,
  onClose,
  title,
  width = 'md',
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel — no overflow set here so child scroll containers work freely */}
      <div
        ref={panelRef}
        className={`
          relative w-full ${WIDTH_CLASSES[width]}
          bg-cream rounded-t-[24px] sm:rounded-[24px]
          shadow-warm-xl
          max-h-[90dvh] flex flex-col
          animate-fade-up
        `}
      >
        {/* Header — fixed, never scrolls */}
        {title && (
          <div className="flex-shrink-0 flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
            <h2 className="font-heading text-xl font-semibold text-navy">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Content — only this div scrolls vertically, leaving x-overflow free */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-6">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
